import { getToken } from '../../../context/auth';
import { toast } from 'react-toastify';

// Helper to save current active session to history list
const saveSessionToHistory = (state) => {
    const { messages, activeSessionId, history } = state;
    if (messages && messages.length > 0 && activeSessionId) {
        const hasValidBotMsg = messages.some(m => m.sender === 'bot' && m.text && m.text.trim() !== '');
        if (!hasValidBotMsg) return null;
        const firstUserMsg = messages.find(m => m.sender === 'user');
        const title = firstUserMsg
            ? (firstUserMsg.text.length > 30 ? firstUserMsg.text.substring(0, 30) + '...' : firstUserMsg.text)
            : 'محادثة جديدة';

        // Preserve original creation timestamp if session exists
        const existingSession = history.find(h => h.id === activeSessionId);
        const timestamp = existingSession ? (existingSession.timestamp || Date.now()) : Date.now();
        const nextTopicRecommendation = state.topicRecommendation || (existingSession ? existingSession.nextTopicRecommendation : null);

        // Extract mainSubject and category from the messages if they exist
        const firstBotMsg = messages.find(m => m.sender === 'bot' && m.isLesson);
        const mainSubject = firstBotMsg ? (firstBotMsg.mainSubject || (existingSession ? existingSession.mainSubject : null)) : (existingSession ? existingSession.mainSubject : null);
        const category = firstBotMsg ? (firstBotMsg.category || (existingSession ? existingSession.category : null)) : (existingSession ? existingSession.category : null);

        // Find existing or make new session in history
        const updatedHistory = [
            { id: activeSessionId, title, messages, timestamp, nextTopicRecommendation, mainSubject, category },
            ...history.filter(h => h.id !== activeSessionId)
        ];
        localStorage.setItem('chatbot_history', JSON.stringify(updatedHistory));
        return updatedHistory;
    }
    return null;
};

export const loadChatHistory = () => dispatch => {
    try {
        const historyStr = localStorage.getItem('chatbot_history');
        const history = historyStr ? JSON.parse(historyStr) : [];
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history });
    } catch (e) {
        console.error('Failed to load chat history from localStorage', e);
    }
};

export const deleteChatHistory = () => dispatch => {
    localStorage.removeItem('chatbot_history');
    localStorage.removeItem('chatbot_active_session_id');
    dispatch({ type: 'CHATBOT_DELETE_HISTORY' });
};

export const deleteChatSession = (sessionId) => (dispatch, getState) => {
    const { history, activeSessionId } = getState().chatbot;
    const updatedHistory = history.filter(h => h.id !== sessionId);
    localStorage.setItem('chatbot_history', JSON.stringify(updatedHistory));
    dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });

    if (activeSessionId === sessionId) {
        localStorage.removeItem('chatbot_active_session_id');
        const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('chatbot_active_session_id', newSessionId);
        dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: newSessionId });
    }
};

export const clearActiveChat = () => dispatch => {
    dispatch({ type: 'CHATBOT_CLEAR_CHAT' });
};

export const startNewChat = () => (dispatch, getState) => {
    // 1. Save current session to history if there are messages
    const updatedHistory = saveSessionToHistory(getState().chatbot);
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }

    // 2. Generate new session ID
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chatbot_active_session_id', newSessionId);
    dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: newSessionId });
};

export const loadChatSession = (session) => (dispatch, getState) => {
    // 1. Save current session first
    const updatedHistory = saveSessionToHistory(getState().chatbot);
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }

    // 2. Load the selected session
    const lastBotMsg = session.messages.slice().reverse().find(m => m.sender === 'bot');
    localStorage.setItem('chatbot_active_session_id', session.id);
    dispatch({
        type: 'CHATBOT_LOAD_SESSION',
        sessionId: session.id,
        messages: session.messages,
        databaseTimeMs: lastBotMsg ? lastBotMsg.databaseTimeMs : 0,
        apiTimeMs: lastBotMsg ? lastBotMsg.apiTimeMs : 0,
        sendTokens: lastBotMsg ? lastBotMsg.sendTokens : 0,
        receivedTokens: lastBotMsg ? lastBotMsg.receivedTokens : 0,
        sendTimeMs: lastBotMsg ? lastBotMsg.sendTimeMs : 0,
        responseTimeMs: lastBotMsg ? lastBotMsg.responseTimeMs : 0,
        serverTimeMs: lastBotMsg ? lastBotMsg.serverTimeMs : 0,
        isFromCache: lastBotMsg ? lastBotMsg.isFromCache : false,
        cacheRemainingTime: lastBotMsg ? lastBotMsg.cacheRemainingTime : null,
        bandwidth: lastBotMsg ? lastBotMsg.bandwidth : 0,
        sendTokensDetails: lastBotMsg ? lastBotMsg.sendTokensDetails : null,
        model: lastBotMsg ? (lastBotMsg.model || lastBotMsg.Model) : null,
        price: lastBotMsg ? (lastBotMsg.price !== undefined ? lastBotMsg.price : lastBotMsg.Price) : 0,
        prompt: lastBotMsg ? (lastBotMsg.prompt || lastBotMsg.Prompt) : null
    });
};

export const initChatbot = () => (dispatch) => {
    const historyStr = localStorage.getItem('chatbot_history');
    const history = historyStr ? JSON.parse(historyStr) : [];
    dispatch({ type: 'CHATBOT_LOAD_HISTORY', history });

    const activeSessionId = localStorage.getItem('chatbot_active_session_id');
    if (activeSessionId && history.length > 0) {
        const activeSession = history.find(h => h.id === activeSessionId);
        if (activeSession) {
            const lastBotMsg = activeSession.messages.slice().reverse().find(m => m.sender === 'bot');
            dispatch({
                type: 'CHATBOT_LOAD_SESSION',
                sessionId: activeSession.id,
                messages: activeSession.messages,
                databaseTimeMs: lastBotMsg ? lastBotMsg.databaseTimeMs : 0,
                apiTimeMs: lastBotMsg ? lastBotMsg.apiTimeMs : 0,
                sendTokens: lastBotMsg ? lastBotMsg.sendTokens : 0,
                receivedTokens: lastBotMsg ? lastBotMsg.receivedTokens : 0,
                sendTimeMs: lastBotMsg ? lastBotMsg.sendTimeMs : 0,
                responseTimeMs: lastBotMsg ? lastBotMsg.responseTimeMs : 0,
                serverTimeMs: lastBotMsg ? lastBotMsg.serverTimeMs : 0,
                isFromCache: lastBotMsg ? lastBotMsg.isFromCache : false,
                cacheRemainingTime: lastBotMsg ? lastBotMsg.cacheRemainingTime : null,
                bandwidth: lastBotMsg ? lastBotMsg.bandwidth : 0,
                sendTokensDetails: lastBotMsg ? lastBotMsg.sendTokensDetails : null,
                model: lastBotMsg ? (lastBotMsg.model || lastBotMsg.Model) : null,
                price: lastBotMsg ? (lastBotMsg.price !== undefined ? lastBotMsg.price : lastBotMsg.Price) : 0,
                prompt: lastBotMsg ? (lastBotMsg.prompt || lastBotMsg.Prompt) : null
            });
            return;
        }
    }
    // If no saved session or not found, start a new one
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chatbot_active_session_id', newSessionId);
    dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: newSessionId });
};

export const stopChatStream = () => (dispatch, getState) => {
    if (window.activeChatbotAbortController) {
        window.activeChatbotAbortController.abort();
        window.activeChatbotAbortController = null;
    }
    dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

    // Save whatever has been loaded so far
    const updatedHistory = saveSessionToHistory(getState().chatbot);
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }
};

export const sendChatMessage = (questionText, passCash = false, model = "gemini", difficulty = "mid", isEvaluation = false, questionsCount = 10) => async (dispatch, getState) => {
    const messages = getState().chatbot.messages || [];
    const isStartChatFinished = messages.length >= 2 && messages.some(m => m.sender === 'bot' && !m.isStreaming);

    if (messages.length > 0 && !isStartChatFinished) {
        console.warn("Cannot send message: start chat is not finished yet.");
        return;
    }

    // Capture previous chat history
    const chatHistory = (messages || [])
        .filter(m => !m.isStreaming && m.text !== "")
        .map(m => ({
            Sender: m.sender,
            Text: m.text
        }));

    const isSubChat = chatHistory.length > 0;

    // 1. Ensure we have an activeSessionId, if not, generate one
    let currentSessionId = getState().chatbot.activeSessionId;
    if (!currentSessionId) {
        currentSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('chatbot_active_session_id', currentSessionId);
        dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: currentSessionId });
    }

    dispatch({ type: 'CHATBOT_SET_LOADING', loading: true });
    dispatch({ type: 'CHATBOT_SET_ERROR', error: null, errorStatus: null, isSubChat });

    // 2. Add user message to state
    dispatch({
        type: 'CHATBOT_ADD_MESSAGE',
        message: { sender: 'user', text: questionText, isEvaluation }
    });

    // 3. Add initial empty bot message with streaming flag
    dispatch({
        type: 'CHATBOT_ADD_MESSAGE',
        message: { sender: 'bot', text: '', isStreaming: true, databaseTimeMs: 0, apiTimeMs: 0, model: model, isLesson: !isSubChat, isEvaluation }
    });

    try {
        const token = getToken();
        const controller = new AbortController();
        window.activeChatbotAbortController = controller;

        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://alarabeyya.com/api';
        const isSubChat = chatHistory.length > 0;
        const chatEndpoint = isSubChat ? `${baseUrl}/chat/sub` : `${baseUrl}/chat`;
        const response = await fetch(chatEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ Question: questionText, PassCash: passCash, Model: model, History: chatHistory, IsSubChat: isSubChat, Difficulty: difficulty, QuestionsCount: questionsCount }),
            signal: controller.signal
        });

        if (!response.ok) {
            let errorText = '';
            try {
                const errData = await response.json();
                if (errData && errData.Status) {
                    dispatch({
                        type: 'CHATBOT_SET_ERROR',
                        error: errData.Answer || `HTTP error! status: ${response.status}`,
                        errorStatus: errData.Status,
                        isSubChat
                    });
                    dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

                    // Save error final state to history
                    const updatedHistory = saveSessionToHistory(getState().chatbot);
                    if (updatedHistory) {
                        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
                    }
                    return;
                }
            } catch (e) {
                try {
                    errorText = await response.text();
                } catch (textErr) { }
            }
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let accumulatedText = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop(); // Keep partial line in buffer

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) continue;

                let jsonStr = trimmed;
                if (trimmed.startsWith('data:')) {
                    jsonStr = trimmed.slice(5).trim();
                }

                if (jsonStr === '[DONE]') {
                    continue;
                }

                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.Status === 'status_update') {
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            statusText: parsed.Answer,
                            text: ''
                        });
                        continue;
                    }
                    if (parsed.Status && parsed.Status !== 'ok') {
                        dispatch({
                            type: 'CHATBOT_SET_ERROR',
                            error: parsed.Answer,
                            errorStatus: parsed.Status,
                            isSubChat
                        });
                        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                        if (window.activeChatbotAbortController) {
                            window.activeChatbotAbortController.abort();
                            window.activeChatbotAbortController = null;
                        }
                        saveSessionToHistory(getState().chatbot);
                        return;
                    } else if (parsed.Answer !== undefined) {
                        accumulatedText += parsed.Answer;
                        if (accumulatedText.includes('CHAT_UNSPOURTED') && !isSubChat) {
                            if (window.activeChatbotAbortController) {
                                window.activeChatbotAbortController.abort();
                                window.activeChatbotAbortController = null;
                            }
                            dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                            dispatch(clearActiveChat());
                            dispatch({
                                type: 'CHATBOT_SET_ERROR',
                                error: 'CHAT_UNSPOURTED',
                                errorStatus: 'unsupported',
                                isSubChat: false
                            });
                            return;
                        }
                        if (parsed.Answer && parsed.Answer.includes('[NEW_CHAT_REQUIRED]')) {
                            if (window.activeChatbotAbortController) {
                                window.activeChatbotAbortController.abort();
                                window.activeChatbotAbortController = null;
                            }
                            dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                            dispatch(startNewChat());
                            toast.info("تم فتح محادثة جديدة تلقائياً لمناقشة موضوعك الجديد.", {
                                position: "bottom-left",
                                autoClose: 5000
                            });
                            return;
                        }
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            text: parsed.Answer,
                            databaseTimeMs: parsed.DatabaseTimeMs,
                            apiTimeMs: parsed.ApiTimeMs,
                            sendTokens: parsed.SendTokens,
                            receivedTokens: parsed.ReceivedTokens,
                            sendTimeMs: parsed.SendTimeMs,
                            responseTimeMs: parsed.ResponseTimeMs,
                            serverTimeMs: parsed.ServerTimeMs,
                            isFromCache: parsed.IsFromCache,
                            cacheRemainingTime: parsed.CacheRemainingTime,
                            searchWords: parsed.SearchWords,
                            bandwidth: parsed.Bandwidth,
                            model: parsed.Model !== undefined ? parsed.Model : parsed.model,
                            price: parsed.Price !== undefined ? parsed.Price : parsed.price,
                            prompt: parsed.Prompt !== undefined ? parsed.Prompt : parsed.prompt,
                            dbQuestionIds: parsed.DbQuestionIds !== undefined ? parsed.DbQuestionIds : parsed.dbQuestionIds,
                            generatedQuestions: parsed.GeneratedQuestions !== undefined ? parsed.GeneratedQuestions : parsed.generatedQuestions,
                            relativeLessons: parsed.RelativeLessons !== undefined ? parsed.RelativeLessons : parsed.relativeLessons,
                            mainSubject: parsed.MainSubject !== undefined ? parsed.MainSubject : parsed.mainSubject,
                            category: parsed.Category !== undefined ? parsed.Category : parsed.category,
                            sendTokensDetails: parsed.SendTokensDetails ? {
                                systemInstruction: parsed.SendTokensDetails.SystemInstruction !== undefined ? parsed.SendTokensDetails.SystemInstruction : parsed.SendTokensDetails.systemInstruction,
                                context: parsed.SendTokensDetails.Context !== undefined ? parsed.SendTokensDetails.Context : parsed.SendTokensDetails.context,
                                lessonsList: parsed.SendTokensDetails.LessonsList !== undefined ? parsed.SendTokensDetails.LessonsList : parsed.SendTokensDetails.lessonsList,
                                question: parsed.SendTokensDetails.Question !== undefined ? parsed.SendTokensDetails.Question : parsed.SendTokensDetails.question
                            } : undefined
                        });
                    }
                } catch (e) {
                    console.warn('Could not parse SSE JSON line', line, e);
                }
            }
        }

        // Process any remaining text in the buffer (if connection closed without newline)
        if (buffer && buffer.trim()) {
            const trimmed = buffer.trim();
            let jsonStr = trimmed;
            if (trimmed.startsWith('data:')) {
                jsonStr = trimmed.slice(5).trim();
            }

            if (jsonStr !== '[DONE]') {
                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.Status === 'status_update') {
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            statusText: parsed.Answer,
                            text: ''
                        });
                        return;
                    }
                    if (parsed.Status && parsed.Status !== 'ok') {
                        dispatch({
                            type: 'CHATBOT_SET_ERROR',
                            error: parsed.Answer,
                            errorStatus: parsed.Status,
                            isSubChat
                        });
                        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                        if (window.activeChatbotAbortController) {
                            window.activeChatbotAbortController.abort();
                            window.activeChatbotAbortController = null;
                        }
                        saveSessionToHistory(getState().chatbot);
                        return;
                    } else if (parsed.Answer !== undefined) {
                        accumulatedText += parsed.Answer;
                        if (accumulatedText.includes('CHAT_UNSPOURTED') && !isSubChat) {
                            if (window.activeChatbotAbortController) {
                                window.activeChatbotAbortController.abort();
                                window.activeChatbotAbortController = null;
                            }
                            dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                            dispatch(clearActiveChat());
                            dispatch({
                                type: 'CHATBOT_SET_ERROR',
                                error: 'CHAT_UNSPOURTED',
                                errorStatus: 'unsupported',
                                isSubChat: false
                            });
                            return;
                        }
                        if (parsed.Answer && parsed.Answer.includes('[NEW_CHAT_REQUIRED]')) {
                            if (window.activeChatbotAbortController) {
                                window.activeChatbotAbortController.abort();
                                window.activeChatbotAbortController = null;
                            }
                            dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                            dispatch(startNewChat());
                            toast.info("تم فتح محادثة جديدة تلقائياً لمناقشة موضوعك الجديد.", {
                                position: "bottom-left",
                                autoClose: 5000
                            });
                            return;
                        }
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            text: parsed.Answer,
                            databaseTimeMs: parsed.DatabaseTimeMs,
                            apiTimeMs: parsed.ApiTimeMs,
                            sendTokens: parsed.SendTokens,
                            receivedTokens: parsed.ReceivedTokens,
                            sendTimeMs: parsed.SendTimeMs,
                            responseTimeMs: parsed.ResponseTimeMs,
                            serverTimeMs: parsed.ServerTimeMs,
                            isFromCache: parsed.IsFromCache,
                            cacheRemainingTime: parsed.CacheRemainingTime,
                            searchWords: parsed.SearchWords,
                            bandwidth: parsed.Bandwidth,
                            model: parsed.Model !== undefined ? parsed.Model : parsed.model,
                            price: parsed.Price !== undefined ? parsed.Price : parsed.price,
                            prompt: parsed.Prompt !== undefined ? parsed.Prompt : parsed.prompt,
                            dbQuestionIds: parsed.DbQuestionIds !== undefined ? parsed.DbQuestionIds : parsed.dbQuestionIds,
                            generatedQuestions: parsed.GeneratedQuestions !== undefined ? parsed.GeneratedQuestions : parsed.generatedQuestions,
                            relativeLessons: parsed.RelativeLessons !== undefined ? parsed.RelativeLessons : parsed.relativeLessons,
                            mainSubject: parsed.MainSubject !== undefined ? parsed.MainSubject : parsed.mainSubject,
                            category: parsed.Category !== undefined ? parsed.Category : parsed.category,
                            sendTokensDetails: parsed.SendTokensDetails ? {
                                systemInstruction: parsed.SendTokensDetails.SystemInstruction !== undefined ? parsed.SendTokensDetails.SystemInstruction : parsed.SendTokensDetails.systemInstruction,
                                context: parsed.SendTokensDetails.Context !== undefined ? parsed.SendTokensDetails.Context : parsed.SendTokensDetails.context,
                                lessonsList: parsed.SendTokensDetails.LessonsList !== undefined ? parsed.SendTokensDetails.LessonsList : parsed.SendTokensDetails.lessonsList,
                                question: parsed.SendTokensDetails.Question !== undefined ? parsed.SendTokensDetails.Question : parsed.SendTokensDetails.question
                            } : undefined
                        });
                    }
                } catch (e) {
                    console.warn('Could not parse remaining buffer JSON', buffer, e);
                }
            }
        }

        // Clean up controller
        window.activeChatbotAbortController = null;
        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

        // Save final result to history
        const updatedHistory = saveSessionToHistory(getState().chatbot);
        if (updatedHistory) {
            dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
        }

    } catch (error) {
        // Don't show error if user aborted the stream
        if (error.name === 'AbortError') {
            console.log('Stream aborted by user');
            return;
        }

        console.error('Error fetching chatbot stream', error);
        dispatch({
            type: 'CHATBOT_SET_ERROR',
            error: error.message || 'حدث خطأ أثناء الاتصال بالخادم. يرجى المحاولة مرة أخرى.',
            errorStatus: 'network_error',
            isSubChat
        });
        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

        // Save error final state to history
        const updatedHistory = saveSessionToHistory(getState().chatbot);
        if (updatedHistory) {
            dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
        }
    }
};

export const regenerateQuestionsOnly = (lessonText, difficulty = "mid", model = "deepseek", questionsCount = 10, dbQuestions = [], existingGeneratedQuestions = []) => async (dispatch, getState) => {
    dispatch({ type: 'CHATBOT_SET_LOADING', loading: true });
    dispatch({ type: 'CHATBOT_SET_ERROR', error: null, errorStatus: null });

    try {
        const token = getToken();
        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://alarabeyya.com/api';
        
        const response = await fetch(`${baseUrl}/chat/regenerate-questions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                LessonText: lessonText, 
                Difficulty: difficulty, 
                Model: model, 
                QuestionsCount: questionsCount,
                DbQuestions: dbQuestions,
                ExistingGeneratedQuestions: existingGeneratedQuestions
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to regenerate questions: ${response.status}`);
        }

        const data = await response.json();
        const dbQuestionIds = data.dbQuestionIds || [];
        const generatedQuestions = data.generatedQuestions || [];

        const newQuestions = [
            ...dbQuestionIds.map(id => ({ Id: String(id), IsDbQuestion: true })),
            ...generatedQuestions
        ];

        const { messages } = getState().chatbot;
        const updatedMessages = messages.map(msg => {
            if (msg.sender === 'bot' && msg.isLesson) {
                // Remove existing script tag if any
                let cleanText = msg.text || '';
                const scriptStartIdx = cleanText.indexOf("<script id='lesson-questions-data'");
                if (scriptStartIdx !== -1) {
                    cleanText = cleanText.substring(0, scriptStartIdx).trim();
                }

                // Append new script tag
                const newScriptTag = `<script id='lesson-questions-data' type='application/json'>${JSON.stringify(newQuestions)}</script>`;
                const newText = `${cleanText}\n\n${newScriptTag}`;

                // Parse new price
                const regenPrice = parseFloat(data.price) || 0;

                // Track regenerations list
                const prevRegens = msg.regenerations || [];
                const newRegens = [...prevRegens, {
                    count: newQuestions.length,
                    price: regenPrice
                }];

                // Track questionsHistory list
                let history = msg.questionsHistory || [];
                if (history.length === 0) {
                    const firstQuestions = [
                        ...(msg.dbQuestionIds || []).map(id => ({ Id: String(id), IsDbQuestion: true })),
                        ...(msg.generatedQuestions || [])
                    ];
                    if (firstQuestions.length > 0) {
                        history.push({
                            timestamp: msg.timestamp || (Date.now() - 10000),
                            dbQuestionIds: msg.dbQuestionIds || [],
                            generatedQuestions: msg.generatedQuestions || [],
                            questions: firstQuestions
                        });
                    }
                }
                history.push({
                    timestamp: Date.now(),
                    dbQuestionIds: dbQuestionIds,
                    generatedQuestions: generatedQuestions,
                    questions: newQuestions
                });

                return {
                    ...msg,
                    text: newText,
                    dbQuestionIds: dbQuestionIds,
                    generatedQuestions: generatedQuestions,
                    regenerations: newRegens,
                    questionsHistory: history
                };
            }
            return msg;
        });

        dispatch({
            type: 'CHATBOT_UPDATE_MESSAGES',
            messages: updatedMessages
        });

        // Trigger history save
        const updatedHistory = saveSessionToHistory({
            ...getState().chatbot,
            messages: updatedMessages
        });
        if (updatedHistory) {
            dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
        }

    } catch (error) {
        console.error("Error regenerating questions", error);
        dispatch({
            type: 'CHATBOT_SET_ERROR',
            error: error.message || 'فشل في إعادة توليد الأسئلة. يرجى المحاولة مرة أخرى.',
            errorStatus: 'regenerate_error'
        });
    } finally {
        dispatch({ type: 'CHATBOT_SET_LOADING', loading: false });
    }
};

export const switchToQuestionSet = (messageIndex, historyIndex) => (dispatch, getState) => {
    const { messages } = getState().chatbot;
    const updatedMessages = messages.map((msg, idx) => {
        if (idx === messageIndex && msg.sender === 'bot' && msg.questionsHistory && msg.questionsHistory[historyIndex]) {
            const selectedSet = msg.questionsHistory[historyIndex];
            const newQuestions = selectedSet.questions || [];

            let cleanText = msg.text || '';
            const scriptStartIdx = cleanText.indexOf("<script id='lesson-questions-data'");
            if (scriptStartIdx !== -1) {
                cleanText = cleanText.substring(0, scriptStartIdx).trim();
            }

            const newScriptTag = `<script id='lesson-questions-data' type='application/json'>${JSON.stringify(newQuestions)}</script>`;
            const newText = `${cleanText}\n\n${newScriptTag}`;

            return {
                ...msg,
                text: newText,
                dbQuestionIds: selectedSet.dbQuestionIds || [],
                generatedQuestions: selectedSet.generatedQuestions || []
            };
        }
        return msg;
    });

    dispatch({
        type: 'CHATBOT_UPDATE_MESSAGES',
        messages: updatedMessages
    });

    // Save to history
    const updatedHistory = saveSessionToHistory({
        ...getState().chatbot,
        messages: updatedMessages
    });
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }
};

export const fetchNextTopicRecommendation = (currentTopic, model = "deepseek") => async (dispatch, getState) => {
    try {
        const token = getToken();
        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://alarabeyya.com/api';
        
        const { history } = getState().chatbot;
        const historyList = history || [];
        const learnedTopics = historyList
            .filter(h => h.title && h.title !== 'محادثة جديدة' && h.title.trim() !== '')
            .map(h => ({
                mainSubject: h.mainSubject || h.title,
                category: h.category || 'أخرى',
                title: h.title
            }));

        const response = await fetch(`${baseUrl}/topic-recommendation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                Topic: currentTopic,
                Model: model,
                LearnedTopics: learnedTopics
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch recommendation: ${response.status}`);
        }

        const data = await response.json();
        if (data && data.topicName) {
            dispatch({
                type: 'CHATBOT_SET_TOPIC_RECOMMENDATION',
                recommendation: data
            });

            // Save recommendation to the active session history immediately
            const updatedHistory = saveSessionToHistory({
                ...getState().chatbot,
                topicRecommendation: data
            });
            if (updatedHistory) {
                dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
            }
        }
    } catch (err) {
        console.error("Error fetching next topic recommendation:", err);
    }
};

